// src/utils/staffSettingsUtils.js
//
// Single source of truth for per-staff settings that affect behavior across the
// whole app: team messaging visibility, dropdown visibility, and the three
// email opt-ins.
//
// Every page that reads these flags should import from here instead of reading
// the raw Firestore fields, so the semantics (and especially the DEFAULTS for
// staff records that predate a flag) stay identical everywhere.
//
// STORAGE SHAPE on users/{uid}:
//   hideFromTeamMessaging : boolean   (true  = hidden from Team Messaging)
//   hideFromScheduling    : boolean   (true  = hidden from Tech Assign dropdowns)
//   hideFromClockTime     : boolean   (true  = hidden on the Clock Time page)
//   canSeeChecklist       : boolean   (true  = this tech sees the Job Checklist)
//   notificationPrefs: {
//     vehicles    : boolean           (Vehicle Expiration reminder emails)
//     jobSchedule : boolean           (New Job Scheduled emails)
//     jobUpdates  : boolean           (Job note / update emails)
//     teamMessages: boolean           (Group Team Message emails)
//   }
//
// NOTE: the Cloud Functions codebase (defaultcodebase/index.js) intentionally
// duplicates the resolver logic below, because functions cannot import from
// src/. If you change a DEFAULT here, change it there too.

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------
//
// Defaults are chosen to PRESERVE existing behavior for staff records that were
// created before a given flag existed:
//
//   jobSchedule / jobUpdates -> true
//       Today every assigned tech with an email gets these. Defaulting to false
//       would silently switch off job emails for the entire existing roster.
//
//   vehicles -> false
//       Vehicle reminders currently go to the notificationRecipients/vehicles
//       list. Staff opt-ins are ADDITIVE on top of that list, so defaulting to
//       true would suddenly blast the whole roster.
//
//   hideFromTeamMessaging / hideFromScheduling -> false (visible)
//       Matches how the existing filters already treat a missing field.

export const STAFF_SETTINGS_DEFAULTS = Object.freeze({
  hideFromTeamMessage: false,
  receiveVehicleExpirationEmails: false,
  receiveJobScheduleEmails: true,
  receiveJobUpdateEmails: true,
  // Default ON: everyone with an email already got group message emails.
  receiveGroupMessageEmails: true,
  showInDropdowns: true,
  showInClockTime: true,
  // Off by default: techs did not see the Job Checklist before this setting.
  // Office roles always see it; see utils/checklistAccess.js.
  canSeeChecklist: false,
});

const toBool = (value, fallback) =>
  value === true ? true : value === false ? false : fallback;

// ---------------------------------------------------------------------------
// Column / field definitions (drives the StaffSettings grid and the Staff modal)
// ---------------------------------------------------------------------------

export const STAFF_SETTING_FIELDS = Object.freeze([
  {
    key: "hideFromTeamMessage",
    label: "Hide From Team Message",
    shortLabel: "Hide Team Msg",
    yesMeans: "Hidden from Team Messaging",
    noMeans: "Shown in Team Messaging",
    help: "Yes removes this person from the Team Messaging recipient list.",
  },
  {
    key: "receiveVehicleExpirationEmails",
    label: "Receive Vehicle Expiration Emails",
    shortLabel: "Vehicle Exp.",
    yesMeans: "Receives vehicle expiration reminders",
    noMeans: "Does not receive vehicle expiration reminders",
    help:
      "Yes adds this person to the daily vehicle inspection / registration / service reminder emails, in addition to the Notifications recipient list.",
  },
  {
    key: "receiveJobScheduleEmails",
    label: "Receive Job Schedule Emails",
    shortLabel: "Job Schedule",
    yesMeans: "Receives New Job Scheduled emails",
    noMeans: "Does not receive New Job Scheduled emails",
    help:
      "Yes emails this person when they are assigned to a newly scheduled job.",
  },
  {
    key: "receiveJobUpdateEmails",
    label: "Receive Job Update Emails",
    shortLabel: "Job Updates",
    yesMeans: "Receives job note / update emails",
    noMeans: "Does not receive job note / update emails",
    help:
      "Yes emails this person when a note is added to a job they are assigned to.",
  },
  {
    key: "receiveGroupMessageEmails",
    label: "Receive Group Message Emails",
    shortLabel: "Group Msg Emails",
    yesMeans: "Emailed when a group Team Message is sent",
    noMeans: "Not emailed for group Team Messages (still sees them in the app)",
    help:
      "No stops the email copy of GROUP Team Messages (Team All, or a message to more than one person). The person still sees the message in Team Messaging, and direct one-to-one messages are still emailed.",
  },
  {
    key: "showInDropdowns",
    label: "Show In Tech Assign Dropdowns",
    shortLabel: "Tech Assign",
    yesMeans: "Appears in Tech Assign dropdowns",
    noMeans: "Hidden from every Tech Assign dropdown",
    help:
      "No hides this person from every Tech Assign / tech dropdown and from scheduling suggestions across the app. Jobs they are already on keep them (shown as \"(hidden)\").",
  },
  {
    key: "showInClockTime",
    label: "Show In Clock Time",
    shortLabel: "Clock Time",
    yesMeans: "Shown on the Clock Time page",
    noMeans: "Hidden on the Clock Time page",
    help:
      "No hides this person's card and removes them from the Add Time / Add Day lists on Clock Time. Their time logs are kept; Clock Time has a \"Show hidden people\" switch.",
  },
  {
    key: "canSeeChecklist",
    label: "Can See Job Checklist",
    shortLabel: "Checklist",
    yesMeans: "Sees the Job Checklist / Punch List on their jobs",
    noMeans: "Does not see the Job Checklist",
    help:
      "Techs only - office staff always see it. Yes shows the Checklist button on this person's job popups so they can check items off and add notes and photos in the field. \"Show the Checklist to every tech\" at the top of this page turns it on for all techs at once.",
  },
]);

export const STAFF_SETTING_KEYS = STAFF_SETTING_FIELDS.map((f) => f.key);

// ---------------------------------------------------------------------------
// Read: user doc -> normalized settings object
// ---------------------------------------------------------------------------

export const resolveStaffSettings = (user = {}) => {
  const prefs = user?.notificationPrefs || {};

  return {
    hideFromTeamMessage: toBool(
      user?.hideFromTeamMessaging,
      STAFF_SETTINGS_DEFAULTS.hideFromTeamMessage
    ),
    receiveVehicleExpirationEmails: toBool(
      prefs?.vehicles,
      STAFF_SETTINGS_DEFAULTS.receiveVehicleExpirationEmails
    ),
    receiveJobScheduleEmails: toBool(
      prefs?.jobSchedule,
      STAFF_SETTINGS_DEFAULTS.receiveJobScheduleEmails
    ),
    receiveJobUpdateEmails: toBool(
      prefs?.jobUpdates,
      STAFF_SETTINGS_DEFAULTS.receiveJobUpdateEmails
    ),
    receiveGroupMessageEmails: toBool(
      prefs?.teamMessages,
      STAFF_SETTINGS_DEFAULTS.receiveGroupMessageEmails
    ),
    // stored inverted: hideFromScheduling === true means hidden
    showInDropdowns: !toBool(
      user?.hideFromScheduling,
      !STAFF_SETTINGS_DEFAULTS.showInDropdowns
    ),
    // stored inverted: hideFromClockTime === true means hidden
    showInClockTime: !toBool(
      user?.hideFromClockTime,
      !STAFF_SETTINGS_DEFAULTS.showInClockTime
    ),
    canSeeChecklist: toBool(
      user?.canSeeChecklist,
      STAFF_SETTINGS_DEFAULTS.canSeeChecklist
    ),
  };
};

// True when the user doc has NO explicit value stored for a given setting yet.
export const isStaffSettingUnset = (user = {}, key = "") => {
  const prefs = user?.notificationPrefs || {};

  switch (key) {
    case "hideFromTeamMessage":
      return typeof user?.hideFromTeamMessaging !== "boolean";
    case "receiveVehicleExpirationEmails":
      return typeof prefs?.vehicles !== "boolean";
    case "receiveJobScheduleEmails":
      return typeof prefs?.jobSchedule !== "boolean";
    case "receiveJobUpdateEmails":
      return typeof prefs?.jobUpdates !== "boolean";
    case "receiveGroupMessageEmails":
      return typeof prefs?.teamMessages !== "boolean";
    case "showInDropdowns":
      return typeof user?.hideFromScheduling !== "boolean";
    case "showInClockTime":
      return typeof user?.hideFromClockTime !== "boolean";
    case "canSeeChecklist":
      return typeof user?.canSeeChecklist !== "boolean";
    default:
      return false;
  }
};

// ---------------------------------------------------------------------------
// Write: normalized settings object -> Firestore update payload
// ---------------------------------------------------------------------------
//
// Uses dotted paths for notificationPrefs so updateDoc MERGES into the existing
// map instead of replacing it. Other prefs already on the record (jobs,
// proposals, etc.) are left untouched.

export const buildStaffSettingsPatch = (settings = {}) => {
  const resolved = { ...STAFF_SETTINGS_DEFAULTS, ...settings };

  return {
    hideFromTeamMessaging: resolved.hideFromTeamMessage === true,
    hideFromScheduling: resolved.showInDropdowns !== true,
    hideFromClockTime: resolved.showInClockTime !== true,
    canSeeChecklist: resolved.canSeeChecklist === true,
    "notificationPrefs.vehicles":
      resolved.receiveVehicleExpirationEmails === true,
    "notificationPrefs.jobSchedule": resolved.receiveJobScheduleEmails === true,
    "notificationPrefs.jobUpdates": resolved.receiveJobUpdateEmails === true,
    "notificationPrefs.teamMessages": resolved.receiveGroupMessageEmails === true,
  };
};

// Same values, but shaped as a nested object. Use this when merging into a
// local in-memory record (e.g. Staff.js editedStaff) rather than writing to
// Firestore with dotted paths.
export const applyStaffSettingsToRecord = (record = {}, settings = {}) => {
  const resolved = { ...resolveStaffSettings(record), ...settings };

  return {
    ...record,
    hideFromTeamMessaging: resolved.hideFromTeamMessage === true,
    hideFromScheduling: resolved.showInDropdowns !== true,
    hideFromClockTime: resolved.showInClockTime !== true,
    canSeeChecklist: resolved.canSeeChecklist === true,
    notificationPrefs: {
      ...(record?.notificationPrefs || {}),
      vehicles: resolved.receiveVehicleExpirationEmails === true,
      jobSchedule: resolved.receiveJobScheduleEmails === true,
      jobUpdates: resolved.receiveJobUpdateEmails === true,
      teamMessages: resolved.receiveGroupMessageEmails === true,
    },
  };
};

// ---------------------------------------------------------------------------
// Convenience predicates for consumer pages
// ---------------------------------------------------------------------------

export const isStaffDeactivated = (user = {}) => {
  const status = String(user?.status || "").toLowerCase();
  return (
    user?.isActive === false ||
    status === "deactivated" ||
    status === "inactive" ||
    status === "terminated"
  );
};

export const showInTeamMessaging = (user = {}) =>
  resolveStaffSettings(user).hideFromTeamMessage !== true;

export const showInDropdowns = (user = {}) =>
  resolveStaffSettings(user).showInDropdowns === true;

export const showInClockTime = (user = {}) =>
  resolveStaffSettings(user).showInClockTime === true;

export const receivesVehicleExpirationEmails = (user = {}) =>
  resolveStaffSettings(user).receiveVehicleExpirationEmails === true;

export const receivesJobScheduleEmails = (user = {}) =>
  resolveStaffSettings(user).receiveJobScheduleEmails === true;

export const receivesJobUpdateEmails = (user = {}) =>
  resolveStaffSettings(user).receiveJobUpdateEmails === true;

export const receivesGroupMessageEmails = (user = {}) =>
  resolveStaffSettings(user).receiveGroupMessageEmails === true;

export const getStaffDisplayName = (user = {}) =>
  `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
  user?.name ||
  user?.email ||
  "";

// Canonical dropdown roster: active staff who are not hidden from dropdowns,
// sorted by display name. Use this anywhere a staff <select> is built.
export const filterStaffForDropdowns = (users = []) =>
  (Array.isArray(users) ? users : [])
    .filter((user) => !isStaffDeactivated(user))
    .filter((user) => showInDropdowns(user))
    .filter((user) => getStaffDisplayName(user))
    .sort((a, b) =>
      getStaffDisplayName(a).localeCompare(getStaffDisplayName(b), undefined, {
        sensitivity: "base",
      })
    );

export const WORKER_TYPE_OPTIONS = Object.freeze([
  "Employee",
  "Sub Contractor",
]);

export const STAFF_STATUS_OPTIONS = Object.freeze([
  "Active",
  "Deactivated",
]);
